/**
 * Layer 1 Now/Next — Tonight's Watch.
 * Binds GET /ai/home-context only: pulse · tonight.line · lamps[].reason
 * · needs_you.head_reason · decision_actions. Continue ≠ Approve.
 */
import { html, go } from "./ui.js";
import { actionLabel } from "./ai-context-rail.js";
import { operatorSay } from "./operator-say.js";

const ORDER = ["judge", "veto", "act", "prove", "stop"];

function pulseOf(home) {
  const data = (home && home.data) || {};
  const tonight = (home && home.tonight) || data.tonight || {};
  const raw = String(tonight.pulse || (home && home.pulse) || "").toLowerCase();
  if (raw === "needs_you" || (home && home.waiting) || tonight.needs_you) return "needs_you";
  if (raw === "acting" || tonight.acting) return "acting";
  return "watching";
}

function lampsOf(home) {
  const data = (home && home.data) || {};
  const tonight = (home && home.tonight) || data.tonight || {};
  const pack = tonight.lamps || data.lamps || (home && home.lamps) || {};
  const src = Array.isArray(pack.lamps) ? pack.lamps : [];
  const byId = new Map(src.map((r) => [String((r && r.id) || "").toLowerCase(), r]));
  return ORDER.map((id) => {
    const hit = byId.get(id) || {};
    const state = String(hit.state || "off").toLowerCase();
    return {
      id,
      label: String(hit.label || id).toUpperCase(),
      state: state === "on" || state === "engaged" || state === "degraded" ? state : "off",
      reason: String(hit.reason || hit.line || ""),
    };
  });
}

function lineOf(home, pulse) {
  const data = (home && home.data) || {};
  const tonight = (home && home.tonight) || data.tonight || {};
  const ny = data.needs_you || {};
  if (pulse === "needs_you") {
    const gap = operatorSay(ny.head_reason || "", "");
    if (gap && gap !== "—") return gap;
    return operatorSay(tonight.line || (home && home.why) || "", "A fact or a person is missing.");
  }
  return operatorSay(
    tonight.line
    || (home && home.why)
    || "",
    pulse === "acting" ? "The Watch is acting." : "Quiet — standing by until a plan needs you.",
  );
}

export function TonightWatchCmc({ home, toast }) {
  const pulse = pulseOf(home);
  const lamps = lampsOf(home);
  const line = lineOf(home, pulse);
  const title = pulse === "needs_you" ? "Needs You" : pulse === "acting" ? "Acting" : "Continuing";
  const badge = pulse === "needs_you" ? "Needs You" : pulse === "acting" ? "Acting" : "On the loop";
  const actions = Array.isArray(home && home.actions) ? home.actions : [];
  const plane = home && home.decisionPlaneAllowed;
  const canAct = plane === true;
  const busy = !!(home && home.busy);
  const run = async (action) => {
    if (!home || typeof home.act !== "function") return;
    await home.act(action, toast);
  };

  let acts;
  if (pulse === "needs_you" && plane === false) {
    acts = html`<p className="croc-tonight__quiet">A plan needs a manager. This seat can only watch.</p>`;
  } else if (canAct && actions.length) {
    acts = html`<div className="croc-tonight__acts" role="group" aria-label="Tonight's next step">
      ${actions.map((a) => html`
        <button
          key=${a.id}
          type="button"
          className=${"btn btn--sm" + (a.id === "continue" ? " btn--go" : " btn--ghost")}
          disabled=${busy}
          data-tonight-act=${a.id}
          onClick=${() => run(a)}>
          ${actionLabel(a)}
        </button>
      `)}
    </div>`;
  } else {
    acts = html`<p className="croc-tonight__quiet">
      Continue stays on the loop.
      <button type="button" className="croc-tonight__jump" onClick=${() => go("/ops/workbench")}>Open Run</button>
    </p>`;
  }

  return html`
    <section className="croc-tonight croc-tonight--cmc" data-tonight="1" data-pulse=${pulse} aria-label="Tonight's Watch">
      <header className="croc-tonight__head">
        <div>
          <p className="croc-tonight__eyebrow">Tonight's Watch</p>
          <h2 className="croc-tonight__title">${title}</h2>
          <p className="croc-tonight__lede">${line}</p>
        </div>
        <span className="croc-tonight__badge">${badge}</span>
      </header>
      <ol className="croc-tonight__lamps" role="list" aria-label="Judge · Veto · Act · Prove · Stop">
        ${lamps.map((lamp) => html`
          <li key=${lamp.id} className="croc-tonight__lamp" data-lamp=${lamp.id} data-state=${lamp.state} title=${operatorSay(lamp.reason, "—")} tabIndex="0">
            <i aria-hidden="true"></i>
            <span className="croc-tonight__lamp-k">${operatorSay(lamp.label, lamp.id)}</span>
            <span className="croc-tonight__lamp-v">${operatorSay(lamp.reason, "—")}</span>
          </li>
        `)}
      </ol>
      ${acts}
    </section>
  `;
}
